
import React from 'react';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from '../ui/carousel';
import TestimonialCard from './TestimonialCard';
import { Testimonial } from '../../data/testimonialData';
import { PartnerTestimonial } from '../../data/partnerTestimonials';
import { motion } from 'framer-motion';
import Autoplay from 'embla-carousel-autoplay';

interface TestimonialCarouselProps {
  customerTestimonials: Testimonial[];
  partnerTestimonials: PartnerTestimonial[];
}

const TestimonialCarousel: React.FC<TestimonialCarouselProps> = ({ 
  customerTestimonials, 
  partnerTestimonials 
}) => {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);

  const plugin = React.useRef(
    Autoplay({ delay: 4000, stopOnInteraction: true })
  );

  // Convert partner testimonials to match Testimonial interface for display
  const convertedPartnerTestimonials: (Testimonial & { cityUrl?: string })[] = partnerTestimonials.map(partner => ({
    id: partner.id + 1000, // Offset IDs to avoid conflicts
    name: partner.name,
    location: partner.company,
    rating: partner.rating,
    text: partner.text,
    service: partner.service,
    cityUrl: partner.cityUrl
  }));

  // Combine and shuffle testimonials (6 customer + 3 partner as per standards)
  const limitedCustomerTestimonials = customerTestimonials.slice(0, 6);
  const limitedPartnerTestimonials = convertedPartnerTestimonials.slice(0, 3);
  
  const allTestimonials = [
    ...limitedCustomerTestimonials,
    ...limitedPartnerTestimonials
  ].sort(() => Math.random() - 0.5); // Randomize order

  React.useEffect(() => {
    if (!api) return;

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <motion.div 
      className="relative max-w-7xl mx-auto px-4"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <Carousel
        setApi={setApi}
        plugins={[plugin.current as any]}
        opts={{
          align: "start",
          loop: true,
          slidesToScroll: 1,
        }}
        className="w-full"
        onMouseEnter={plugin.current.stop}
        onMouseLeave={plugin.current.reset}
      >
        <CarouselContent className="-ml-4 flex flex-nowrap">
          {allTestimonials.map((testimonial, index) => (
            <CarouselItem key={testimonial.id} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3 flex-shrink-0">
              <TestimonialCard 
                testimonial={testimonial} 
                isPartner={'cityUrl' in testimonial && testimonial.cityUrl !== undefined}
                partnerUrl={'cityUrl' in testimonial ? testimonial.cityUrl as string : undefined}
                index={index}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="absolute -left-12 top-1/2 -translate-y-1/2 hidden lg:block">
          <CarouselPrevious className="static translate-y-0" />
        </div>
        <div className="absolute -right-12 top-1/2 -translate-y-1/2 hidden lg:block">
          <CarouselNext className="static translate-y-0" />
        </div>
      </Carousel>
      
      {/* Navigation Dots */}
      <div className="flex justify-center gap-2 mt-8">
        {Array.from({ length: count }).map((_, index) => (
          <button
            key={index}
            onClick={() => api?.scrollTo(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === current 
                ? 'w-8 bg-gold' 
                : 'w-2 bg-navy/20 hover:bg-navy/40'
            }`}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default TestimonialCarousel;
